<!-- Раскрытая левая панель ПЛАНа: просмотр расчёта и правка исходных данных. -->
<template>
  <section class="plan-panel">
    <PlanCalculationTabs
      v-model="activeTab"
      :changed-count="changedRows.length"
      :history-count="historyCount"
    />

    <PlanCalculationBreakdown
      v-if="activeTab === 'calculation'"
      :breakdown="currentPlan.breakdown || []"
      :result="currentPlan.result"
      :result-name="resultName"
      :result-unit="resultUnit"
      :result-tone="resultTone"
      :preset-name="currentPlan.preset_snapshot?.name || 'Калькулятор'"
      :preset-params="currentPlan.preset_snapshot?.params || []"
      :version="currentPlan.version"
      :changed-rows="changedRows"
    />

    <PlanCalculationInputsEditor
      v-else-if="activeTab === 'norms'"
      :rows="parameterRows"
      :draft="draft"
      :next-version="Number(currentPlan.version) + 1"
      :invalid="invalidDraft"
      :is-calculating="isCalculating"
      :changed-count="changedRows.length"
      :result-name="resultName"
      :editable="editable"
      @update-value="updateDraft"
      @restore="restoreParameter"
      @reset-all="resetDraftToCalculator"
      @recalculate="recalculate"
    />

    <PlanCalculationHistory
      v-else
      :calculations="calculations"
      :changed-rows="changedRows"
      :next-version="Number(currentPlan.version) + 1"
      :has-more="hasMoreHistory"
      :is-loading-more="isLoadingHistory"
      @load-more="$emit('load-more-history')"
    />
  </section>
</template>

<script>
import PlanCalculationBreakdown from "@/components/Order/PlanCalculation/Breakdown.vue";
import PlanCalculationHistory from "@/components/Order/PlanCalculation/History.vue";
import PlanCalculationInputsEditor from "@/components/Order/PlanCalculation/InputsEditor.vue";
import PlanCalculationTabs from "@/components/Order/PlanCalculation/Tabs.vue";
import {
  buildParameterDraft,
  buildPlanParameterRows,
  buildPresetParamsPayload,
  hasInvalidParameterDraft,
  isPlanParameterDifferent,
} from "@/utils/orderCalculation/overrideUtils";
import { hasCalculatorPresetChanged } from "@/utils/orderCalculation/historyUtils";

export default {
  name: "OrderPlanCalculationPanel",

  components: {
    PlanCalculationBreakdown,
    PlanCalculationHistory,
    PlanCalculationInputsEditor,
    PlanCalculationTabs,
  },

  props: {
    currentPlan: { type: Object, required: true },
    calculations: { type: Array, default: () => [] },
    historyCount: { type: Number, default: 0 },
    hasMoreHistory: { type: Boolean, default: false },
    isLoadingHistory: { type: Boolean, default: false },
    resultName: { type: String, default: "Результат" },
    resultUnit: { type: String, default: "rub" },
    resultTone: {
      type: String,
      default: "neutral",
      validator: (value) => ["negative", "neutral", "positive"].includes(value),
    },
    isCalculating: { type: Boolean, default: false },
    editable: { type: Boolean, default: false },
  },

  emits: ["recalculate", "load-more-history", "changed-count"],

  data() {
    return {
      activeTab: "calculation",
      draft: {},
    };
  },

  computed: {
    parameterRows() {
      return buildPlanParameterRows(this.currentPlan);
    },
    changedRows() {
      return this.parameterRows
        .filter((row) => isPlanParameterDifferent(this.draft[row.code], row.originalValue))
        .map((row) => ({ ...row, value: this.draft[row.code] }));
    },
    invalidDraft() {
      return hasInvalidParameterDraft(this.parameterRows, this.draft);
    },
  },

  watch: {
    // У заказа может быть та же версия, что и у предыдущего открытого заказа.
    // Идентификатор гарантированно отделяет их черновики друг от друга.
    "currentPlan.id"() {
      this.resetDraft();
    },
    "currentPlan.calculator_preset_id"(value, previousValue) {
      // После смены калькулятора сначала показываем пользователю новый результат,
      // а не оставляем его в исходных данных уже другого калькулятора.
      if (hasCalculatorPresetChanged(value, previousValue)) {
        this.activeTab = "calculation";
      }
    },
    changedRows: {
      handler(rows) {
        this.$emit("changed-count", rows.length);
      },
      immediate: true,
    },
  },

  created() {
    this.resetDraft();
  },

  methods: {
    resetDraft() {
      this.draft = buildParameterDraft(this.parameterRows);
    },
    resetDraftToCalculator() {
      this.draft = this.parameterRows.reduce((draft, row) => {
        draft[row.code] = row.originalValue == null ? "" : String(row.originalValue);
        return draft;
      }, {});
    },
    updateDraft({ code, value }) {
      this.draft = { ...this.draft, [code]: value };
    },
    restoreParameter(row) {
      this.updateDraft({ code: row.code, value: row.originalValue });
    },
    recalculate() {
      const presetParams = buildPresetParamsPayload(this.parameterRows, this.draft);

      this.$emit("recalculate", {
        presetId: this.currentPlan.calculator_preset_id,
        presetParams,
      });
    },
  },
};
</script>

<style scoped>
.plan-panel {
  overflow: hidden;
  border: 1px solid #e0e7ff;
  border-radius: 10px;
}
</style>
